// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import "./AdminHome.css";
import "./EditDocument.css";
import { useNavPinned } from "./useNavPinned";
import AdminTopNav from "./AdminTopNav";
import { API_ENDPOINTS } from "../config/api";
import TipTapEditor from "./TipTapEditor";
import DynamicField from "./DynamicField";
import { getCollectionSchema, isFieldRequired, getFieldType, getFieldLabel, validateRequiredFields, getFieldOrder } from "./collectionSchemas";
// eslint-disable-next-line no-unused-vars
import SidebarAd from "../Blog/SidebarAd";


export default function EditDocument() {
    const { collectionName, documentId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [navPinned, setNavPinned] = useNavPinned();
    const [isScheduled, setIsScheduled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [documentData, setDocumentData] = useState(null);
    const [editData, setEditData] = useState({});
    const isInitialized = useRef(false);
    const lastDocRef = useRef("");
    const [isSaving, setIsSaving] = useState(false);
    const [isSaveSuccess, setIsSaveSuccess] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [showFixedBar, setShowFixedBar] = useState(true);
    const _buttonBarObserver = useRef(null);
    const buttonBarRef = useCallback((node) => {
        if (_buttonBarObserver.current) {
            _buttonBarObserver.current.disconnect();
            _buttonBarObserver.current = null;
        }
        if (!node) return;
        _buttonBarObserver.current = new IntersectionObserver(
            ([entry]) => setShowFixedBar(!entry.isIntersecting),
            { threshold: 0 }
        );
        _buttonBarObserver.current.observe(node);
    }, []);
    const [message, setMessage] = useState("");
    const [timerProgress, setTimerProgress] = useState(100);

    const [validationErrors, setValidationErrors] = useState([]);
    const [jsonErrors, setJsonErrors] = useState({}); // Track JSON parsing errors per field
    const [authorOptions, setAuthorOptions] = useState([]); // Options for Author dropdown
    const [categoryOptions, setCategoryOptions] = useState([]); // Options for Category dropdown
    const [allPostTopicOptions, setAllPostTopicOptions] = useState([]); // Store all PostTopics
    const [postTopicOptions, setPostTopicOptions] = useState([]); // Filtered PostTopics based on category
    const [postTopicCategoryOptions, setPostTopicCategoryOptions] = useState([]); // Options for Category dropdown in PostTopics
    const [referenceCache, setReferenceCache] = useState({}); // Cache for resolved ObjectId references
    const [resolvedValues, setResolvedValues] = useState({}); // Store resolved ObjectId values
    const [regionOptions, setRegionOptions] = useState([]); // eslint-disable-line no-unused-vars
    const [productOptions, setProductOptions] = useState([]); // Store Products for ProductPrices
    
    // Get user role from localStorage and check if in view mode
    const getUserRole = () => {
        try {
            const userStr = sessionStorage.getItem("user");
            if (userStr) {
                const user = JSON.parse(userStr);
                return user.role || null;
            }
        } catch (e) {
            console.error("Error parsing user data:", e);
        }
        return null;
    };
    
    const userRole = getUserRole();
    const isAdmin = userRole === 'admin'; // eslint-disable-line no-unused-vars
    const isStaff = userRole === 'staff';
    // Check if URL contains 'view' or user is staff (view-only mode)
    const isViewMode = location.pathname.includes('/view/') || isStaff;
    
    // Detect database from URL path
    let databaseType = 'creddb';
    let databaseName = 'CredDb';
    if (location.pathname.includes('/blogdb/')) {
        databaseType = 'blogdb';
        databaseName = 'BlogDb';
    } else if (location.pathname.includes('/newsletterdb/')) {
        databaseType = 'newsletterdb';
        databaseName = 'NewsLetterDb';
    } else if (location.pathname.includes('/podcastdb/')) {
        databaseType = 'podcastdb';
        databaseName = 'PodcastDb';
    } else if (location.pathname.includes('/storedb/')) {
        databaseType = 'storedb';
        databaseName = 'StoreDb';
    } else if (location.pathname.includes('/sponsorshipdb/')) {
        databaseType = 'sponsorshipdb';
        databaseName = 'SponsorshipDb';
    } else if (location.pathname.includes('/userthoughts/')) {
        databaseType = 'userthoughts';
        databaseName = 'UserThoughts';
    } else if (location.pathname.includes('/newsroomdb/')) {
        databaseType = 'newsroomdb';
        databaseName = 'NewsRoomDb';
    } else if (location.pathname.includes('/learnwithai/')) {
        databaseType = 'learnwithai';
        databaseName = 'LearnWithAI';
    } else if (location.pathname.includes('/socialmediadb/')) {
        databaseType = 'socialmediadb';
        databaseName = 'SocialMediaDb';
    } else if (location.pathname.includes('/affiliateproductsdb/')) {
        databaseType = 'affiliateproductsdb';
        databaseName = 'AffiliateProductsDb';
    }

    // Builds the correct API URL for document operations depending on which DB the collection lives in.
    // CredDb and most others share the generic /admin/database/:collection/:id path.
    // NewsRoomDb and LearnWithAI have their own sub-paths on the backend.
    const getDocUrl = (collection, docId) => {
        if (databaseType === 'newsroomdb') {
            const base = `${API_ENDPOINTS.ADMIN.DATABASE_NEWSROOMDB}/${encodeURIComponent(collection)}`;
            return docId ? `${base}/${encodeURIComponent(docId)}` : base;
        }
        if (databaseType === 'learnwithai') {
            const base = `${API_ENDPOINTS.ADMIN.DATABASE_LEARNWITHAI}/${encodeURIComponent(collection)}`;
            return docId ? `${base}/${encodeURIComponent(docId)}` : base;
        }
        if (databaseType === 'socialmediadb') {
            const base = `${API_ENDPOINTS.ADMIN.DATABASE_SOCIALMEDIADB}/${encodeURIComponent(collection)}`;
            return docId ? `${base}/${encodeURIComponent(docId)}` : base;
        }
        if (databaseType === 'affiliateproductsdb') {
            const base = `${API_ENDPOINTS.ADMIN.DATABASE_AFFILIATEPRODUCTSDB}/${encodeURIComponent(collection)}`;
            return docId ? `${base}/${encodeURIComponent(docId)}` : base;
        }
        return docId
            ? API_ENDPOINTS.ADMIN.COLLECTION_GET(collection, docId)
            : API_ENDPOINTS.ADMIN.COLLECTION_CREATE(collection);
    };

    const getAuthHeaders = () => {
        const token = sessionStorage.getItem("token");
        return {
            "Content-Type": "application/json",
            "Accept": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };
    };

    // Save current unsaved form state to sessionStorage before navigating away
    // (called by DynamicField/TipTapEditor when user clicks "Add New" in a popup)
    const saveFormStateToSession = useCallback(() => {
        const key = `editDoc_${collectionName}_${documentId}_unsaved`;
        sessionStorage.setItem(key, JSON.stringify(editData));
    }, [editData, collectionName, documentId]);

    const fetchDocument = async () => {
        setLoading(true);
        setError("");
        try {
            const url = getDocUrl(collectionName, documentId);

            const res = await fetch(url, {
                headers: getAuthHeaders(),
                credentials: "include",
                cache: "no-store"
            });
            
            const contentType = res.headers.get("content-type");
            
            if (!res.ok) {
                let errorMessage = `Failed to load document (${res.status})`;
                
                if (contentType && contentType.includes("application/json")) {
                    try {
                        const errorJson = await res.json();
                        errorMessage = errorJson.message || errorMessage;
                    } catch {
                        // If JSON parsing fails, use default message
                    }
                } else {
                    // If HTML response, extract text content
                    const text = await res.text();
                    // Try to extract meaningful error from HTML
                    const match = text.match(/<pre>(.*?)<\/pre>/i);
                    if (match && match[1]) {
                        errorMessage = match[1];
                    } else if (text.length < 200) {
                        errorMessage = text;
                    }
                }
                
                throw new Error(errorMessage);
            }
            
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error("Server returned non-JSON response. Please ensure the backend server is running and the route is correctly configured.");
            }
            
            const json = await res.json();
            
            if (!json.data) {
                throw new Error("Document not found");
            }
            
            let fetchedData = json.data;

            // Auto-publish scheduled posts when admin views them
            // Mirrors the auto-publish logic in the public blog/podcast routes
            const isSchedulableCollection =
                collectionName === 'BlogContent' || collectionName === 'PodcastsAudioDetails';
            const scheduleDateField =
                collectionName === 'BlogContent' ? 'createdAt' :
                collectionName === 'PodcastsAudioDetails' ? 'date' : null;

            if (
                isSchedulableCollection &&
                scheduleDateField &&
                fetchedData[scheduleDateField] &&
                fetchedData.isPublished === false
            ) {
                const scheduledDate = new Date(fetchedData[scheduleDateField]);
                if (scheduledDate <= new Date()) {
                    // Scheduled date has passed — auto-publish now
                    try {
                        const patchUrl = getDocUrl(collectionName, documentId);
                        await fetch(patchUrl, {
                            method: 'PATCH',
                            headers: getAuthHeaders(),
                            body: JSON.stringify({
                                isPublished: true,
                                publishedAt: fetchedData.publishedAt || fetchedData.createdAt || new Date().toISOString()
                            }),
                            credentials: 'include',
                        });
                        fetchedData = { ...fetchedData, isPublished: true };
                    } catch (autoPublishErr) {
                        console.error('Auto-publish error:', autoPublishErr);
                    }
                }
            }

            setDocumentData(fetchedData);

            // Initialize editData with all schema fields, using fetched data or defaults
            const schema = getCollectionSchema(collectionName);
            const initialEditData = { ...fetchedData };
            
            if (schema) {
                // Add all schema fields with appropriate defaults if they don't exist
                Object.keys(schema.fieldTypes).forEach(field => {
                    if (initialEditData[field] === undefined || initialEditData[field] === null) {
                        const fieldType = schema.fieldTypes[field];
                        switch (fieldType) {
                            case 'number':
                                initialEditData[field] = '';
                                break;
                            case 'boolean':
                                initialEditData[field] = false;
                                break;
                            case 'array':
                                initialEditData[field] = [];
                                break;
                            case 'date':
                                initialEditData[field] = new Date().toISOString();
                                break;
                            case 'object':
                                // For coverImage, category, postTopic - initialize as empty object
                                if (field === 'coverImage') {
                                    initialEditData[field] = { url: '', fileName: '', imageTitle: '' };
                                } else {
                                    initialEditData[field] = {};
                                }
                                break;
                            case 'objectid':
                                initialEditData[field] = '';
                                break;
                            case 'richtext':
                                // Check if this is description field in ProductDetails
                                if (field === 'description' && collectionName === 'ProductDetails') {
                                    initialEditData[field] = { html: '', json: { type: 'doc', content: [] } };
                                } else {
                                    initialEditData[field] = { html: '', json: { type: 'doc', content: [] } };
                                }
                                break;
                            default:
                                initialEditData[field] = '';
                        }
                    }
                });
            }
            
            // Restore unsaved form state if we navigated away to create a related item
            const savedStateKey = `editDoc_${collectionName}_${documentId}_unsaved`;
            const savedStr = sessionStorage.getItem(savedStateKey);
            if (savedStr) {
                try {
                    const saved = JSON.parse(savedStr);
                    setEditData(saved);
                } catch {
                    setEditData(initialEditData);
                }
                sessionStorage.removeItem(savedStateKey);
            } else {
                setEditData(initialEditData);
            }

            // Initialize isScheduled state
            const dateField = collectionName === 'BlogContent' ? 'createdAt' : (collectionName === 'PodcastsAudioDetails' ? 'date' : null);
            if (dateField && initialEditData[dateField]) {
                const dateVal = new Date(initialEditData[dateField]);
                const now = new Date();
                // If date is in the future (more than 1 minute ahead to be safe against slight clocks diffs), set as scheduled
                // Actually user said "Post now" sets date to NOW.
                // If existing date is roughly NOW or PAST, it's not scheduled.
                // If it is FUTURE, it is scheduled.
                if (dateVal > now) {
                    setIsScheduled(true);
                } else {
                    setIsScheduled(false);
                }
            }
        } catch (e) {
            setError(e.message || "Failed to load document");
        } finally {
            setLoading(false);
        }
    };

    // Fetch reference data for dropdowns when editing BlogContent, PostTopics or ProductPrices
    const fetchReferenceData = async () => {
        const lowerCollection = collectionName ? collectionName.toLowerCase() : '';
        if (lowerCollection !== 'blogcontent' && lowerCollection !== 'posttopics' && lowerCollection !== 'productprices') return;
        
        try {
            const token = sessionStorage.getItem("token");
            const headers = {
                "Content-Type": "application/json",
                "Accept": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            };

            // Fetch Category for BlogContent or PostTopics
            try {
                const categoryRes = await fetch(API_ENDPOINTS.ADMIN.DATABASE_COLLECTION('Category'), {
                    headers,
                    credentials: "include",
                    cache: "no-store"
                });
                if (categoryRes.ok) {
                    const categoryJson = await categoryRes.json();
                    const categories = (Array.isArray(categoryJson.data) ? categoryJson.data : [])
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    if (lowerCollection === 'blogcontent') {
                        setCategoryOptions(categories);
                    } else if (lowerCollection === 'posttopics') {
                        setPostTopicCategoryOptions(categories);
                    }
                }
            } catch (err) {
            }

            // Fetch AuthorDetails and PostTopics only for BlogContent
            if (lowerCollection === 'blogcontent') {
                // Fetch AuthorDetails
                try {
                    const authorRes = await fetch(API_ENDPOINTS.ADMIN.DATABASE_COLLECTION('AuthorDetails'), {
                        headers,
                        credentials: "include",
                        cache: "no-store"
                    });
                    if (authorRes.ok) {
                        const authorJson = await authorRes.json();
                        const authors = (Array.isArray(authorJson.data) ? authorJson.data : [])
                            .sort((a, b) => (a.name || a.authorName || '').localeCompare(b.name || b.authorName || ''));
                        setAuthorOptions(authors);
                    }
                } catch (err) {
                }

                // Fetch PostTopics
                try {
                    const postTopicRes = await fetch(API_ENDPOINTS.ADMIN.DATABASE_COLLECTION('PostTopics'), {
                        headers,
                        credentials: "include",
                        cache: "no-store"
                    });
                    if (postTopicRes.ok) {
                        const postTopicJson = await postTopicRes.json();
                        const allTopics = (Array.isArray(postTopicJson.data) ? postTopicJson.data : [])
                            .sort((a, b) => (a.topic || a.name || '').localeCompare(b.topic || b.name || ''));
                        setAllPostTopicOptions(allTopics);
                        // Initially set all topics, will be filtered when category is selected
                        setPostTopicOptions(allTopics);
                    }
                } catch (err) {
                }
            }

            // Fetch Regions for ProductPrices
            if (lowerCollection === 'productprices') {
                try {
                    const regionRes = await fetch(API_ENDPOINTS.ADMIN.DATABASE_COLLECTION('Regions'), {
                        headers,
                        credentials: "include",
                        cache: "no-store"
                    });
                    if (regionRes.ok) {
                        const regionJson = await regionRes.json();
                        const regions = (Array.isArray(regionJson.data) ? regionJson.data : [])
                            .sort((a, b) => (a.name || a.country || '').localeCompare(b.name || b.country || ''));
                        setRegionOptions(regions);
                    }
                } catch (err) {
                    console.error("Error fetching regions:", err);
                }

                try {
                    const productRes = await fetch(API_ENDPOINTS.ADMIN.DATABASE_COLLECTION('ProductDetails'), {
                        headers,
                        credentials: "include",
                        cache: "no-store"
                    });
                    if (productRes.ok) {
                        const productJson = await productRes.json();
                        const products = (Array.isArray(productJson.data) ? productJson.data : [])
                            .sort((a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || ''));
                        setProductOptions(products);
                    }
                } catch (err) {
                    console.error("Error fetching products:", err);
                }
            }
        } catch (err) {
        }
    };

    // Mapping of field names to their reference collections and display fields
    const getReferenceMapping = (collectionName, fieldName) => {
        const mappings = {
            'BlogContent': {
                'author': { collection: 'AuthorDetails', displayField: 'name', dbType: 'blogdb' },
                'category': { collection: 'Category', displayField: 'name', dbType: 'blogdb' },
                'postTopic': { collection: 'PostTopics', displayField: 'topic', dbType: 'blogdb' }
            },
            'PostTopics': {
                'whichCategory': { collection: 'Category', displayField: 'name', dbType: 'blogdb' }
            },
            'Users': {
                'userId': { collection: 'Users', displayField: 'name', dbType: 'creddb' }
            },
            'PurchaseOrders': {
                'userId': { collection: 'Users', displayField: 'name', dbType: 'creddb' }
            },
            'PodcastsAudioDetails': {
                'hosts': { collection: 'PodcastGuests', displayField: 'name', dbType: 'podcastdb', isArray: true }
            },
            'CouponCode': {
                'product': { collection: 'ProductDetails', displayField: 'title', dbType: 'storedb', isArray: true }
            }
        };
        if (mappings[collectionName] && mappings[collectionName][fieldName]) {
            return mappings[collectionName][fieldName];
        }
        return null;
    };

    // Fetch referenced document by ObjectId
    const fetchReference = async (refId, refCollection, dbType) => {
        if (!refId || !refCollection) return null;
        
        const cacheKey = `${refCollection}_${refId}`;
        if (referenceCache[cacheKey]) {
            return referenceCache[cacheKey];
        }

        try {
            const url = API_ENDPOINTS.ADMIN.COLLECTION_GET(refCollection, refId);
            const res = await fetch(url, {
                headers: getAuthHeaders(),
                credentials: "include",
                cache: "no-store"
            });

            if (res.ok) {
                const json = await res.json();
                const doc = json.data;
                if (doc) {
                    setReferenceCache(prev => ({ ...prev, [cacheKey]: doc }));
                    return doc;
                }
            }
        } catch (error) {
            console.error(`Error fetching reference ${refCollection}/${refId}:`, error);
        }
        return null;
    };

    // Filter PostTopics based on selected category
    const filterPostTopicsByCategory = (selectedCategory, allTopics) => {
        if (!selectedCategory) {
            // If no category selected, show all topics
            setPostTopicOptions(allTopics);
            return;
        }
        
        const categoryId = typeof selectedCategory === 'object' && selectedCategory._id 
            ? String(selectedCategory._id) 
            : String(selectedCategory);
        
        const filtered = allTopics.filter(topic => {
            if (!topic.whichCategory) return false;
            const topicCategoryId = typeof topic.whichCategory === 'object' && topic.whichCategory._id 
                ? String(topic.whichCategory._id) 
                : String(topic.whichCategory);
            return topicCategoryId === categoryId;
        });
        
        setPostTopicOptions(filtered);
    };

    useEffect(() => {
        if (isInitialized.current && lastDocRef.current === `${collectionName}_${documentId}`) return;
        
        if (collectionName && documentId) {
            fetchDocument();
        }
        if (collectionName === 'BlogContent' || collectionName === 'PostTopics') {
            fetchReferenceData();
        }
        
        isInitialized.current = true;
        lastDocRef.current = `${collectionName}_${documentId}`;
    }, [collectionName, documentId]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Filter PostTopics when document data is loaded and category is available
    useEffect(() => {
        if (collectionName === 'BlogContent' && allPostTopicOptions.length > 0 && editData && editData.category) {
            filterPostTopicsByCategory(editData.category, allPostTopicOptions);
        }
    }, [documentData, allPostTopicOptions, collectionName, editData]);

    // Resolve all ObjectId references when document data is loaded (for view mode)
    useEffect(() => {
        if (!isViewMode || !documentData) return;
        
        const resolveAllReferences = async () => {
            const resolved = {};
            const keys = Object.keys(documentData);
            
            for (const key of keys) {
                const value = documentData[key];
                const fieldType = getFieldType(collectionName, key);
                const mapping = getReferenceMapping(collectionName, key);
                
                // Check if value is already populated (has name/title/topic)
                if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                    if (value.name || value.title || value.topic) {
                        // Already populated, use the name/title/topic
                        resolved[key] = value.name || value.title || value.topic;
                        continue;
                    }
                }
                
                if (mapping) {
                    if (mapping.isArray && Array.isArray(value)) {
                        // Resolve array of ObjectIds
                        const resolvedNames = await Promise.all(
                            value.map(async (item) => {
                                // If item already has name/title, use it
                                if (typeof item === 'object' && item !== null) {
                                    if (item.name || item.title || item.topic) {
                                        return item.name || item.title || item.topic;
                                    }
                                    if (item._id) {
                                        const refDoc = await fetchReference(String(item._id), mapping.collection, mapping.dbType);
                                        return refDoc ? (refDoc[mapping.displayField] || refDoc.name || refDoc.title || refDoc.topic || String(item._id)) : String(item._id);
                                    }
                                }
                                if (typeof item === 'string' && /^[0-9a-fA-F]{24}$/.test(item)) {
                                    const refDoc = await fetchReference(item, mapping.collection, mapping.dbType);
                                    return refDoc ? (refDoc[mapping.displayField] || refDoc.name || refDoc.title || refDoc.topic || item) : item;
                                }
                                return item;
                            })
                        );
                        resolved[key] = resolvedNames;
                    } else if (typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value)) {
                        // Resolve single ObjectId
                        const refDoc = await fetchReference(value, mapping.collection, mapping.dbType);
                        resolved[key] = refDoc ? (refDoc[mapping.displayField] || refDoc.name || refDoc.title || refDoc.topic || value) : value;
                    } else if (typeof value === 'object' && value !== null && value._id) {
                        // Resolve ObjectId from object
                        const refDoc = await fetchReference(String(value._id), mapping.collection, mapping.dbType);
                        resolved[key] = refDoc ? (refDoc[mapping.displayField] || refDoc.name || refDoc.title || refDoc.topic || String(value._id)) : String(value._id);
                    }
                } else if (fieldType === 'objectid' && typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value)) {
                    // Generic ObjectId field - try common collections based on field name
                    let refCollection = null;
                    let displayField = 'name';
                    
                    // Try to infer collection from field name
                    if (key.toLowerCase().includes('author')) {
                        refCollection = 'AuthorDetails';
                    } else if (key.toLowerCase().includes('category')) {
                        refCollection = 'Category';
                    } else if (key.toLowerCase().includes('topic')) {
                        refCollection = 'PostTopics';
                        displayField = 'topic';
                    } else if (key.toLowerCase().includes('user')) {
                        refCollection = 'Users';
                    } else if (key.toLowerCase().includes('product')) {
                        refCollection = 'ProductDetails';
                        displayField = 'title';
                    } else if (key.toLowerCase().includes('guest') || key.toLowerCase().includes('host')) {
                        refCollection = 'PodcastGuests';
                    }
                    
                    if (refCollection) {
                        // Try to determine database type
                        let dbType = 'blogdb';
                        if (refCollection === 'Users') dbType = 'creddb';
                        else if (refCollection === 'ProductDetails') dbType = 'storedb';
                        else if (refCollection === 'PodcastGuests') dbType = 'podcastdb';
                        
                        const refDoc = await fetchReference(value, refCollection, dbType);
                        if (refDoc) {
                            resolved[key] = refDoc[displayField] || refDoc.name || refDoc.title || refDoc.topic || value;
                        }
                    }
                }
            }
            
            setResolvedValues(resolved);
        };
        
        resolveAllReferences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentData, isViewMode, collectionName]);

    // Timer for auto-hiding messages
    useEffect(() => {
        if (showMessage) {
            setTimerProgress(100);
            const duration = 3000; // 3 seconds
            const interval = 50; // Update every 50ms for performance
            const steps = duration / interval;
            const decrement = 100 / steps;
            
            const timer = setInterval(() => {
                setTimerProgress(prev => {
                    if (prev <= 0) {
                        clearInterval(timer);
                        setShowMessage(false);
                        return 0;
                    }
                    return prev - decrement;
                });
            }, interval);
            
            return () => clearInterval(timer);
        }
    }, [showMessage]);



    // Recursively trim leading/trailing whitespace from all string fields
    const trimStringValues = (obj) => {
        if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return obj;
        const result = {};
        Object.keys(obj).forEach(key => {
            const val = obj[key];
            if (typeof val === 'string') {
                result[key] = val.trim();
            } else if (Array.isArray(val)) {
                result[key] = val.map(item => (typeof item === 'string' ? item.trim() : item));
            } else {
                result[key] = val;
            }
        });
        return result;
    };

    const handleSave = async () => {
        // Ensure editData is valid before proceeding
        if (!editData || typeof editData !== 'object' || Array.isArray(editData)) {
            setMessage('Error: Invalid data format');
            setShowMessage(true);
            return;
        }
        
        setIsSaving(true);
        setValidationErrors([]);
        
        try {
            // Validate required fields before saving
            try {
                const schema = getCollectionSchema(collectionName);
                if (schema) {
                    try {
                        const validation = validateRequiredFields(collectionName, editData);
                        if (!validation.valid) {
                            setValidationErrors(validation.errors);
                            setMessage(`Please fill all required fields: ${validation.errors.join(', ')}`);
                            setShowMessage(true);
                            setIsSaving(false);
                            return;
                        }
                    } catch (error) {
                        setMessage(`Error validating fields: ${error.message || 'Validation failed'}`);
                        setShowMessage(true);
                        setIsSaving(false);
                        return;
                    }
                }
            } catch (validationError) {
                setMessage(`Error: ${validationError.message || 'Validation error occurred'}`);
                setShowMessage(true);
                setIsSaving(false);
                return;
            }
            
            // Remove _id from edit data if present (shouldn't be modified)
            let updateData;
            try {
                const { _id, id: documentIdField, __v, ...rest } = editData;
                updateData = trimStringValues(rest);
            } catch (destructureError) {
                setMessage('Error: Invalid data structure');
                setShowMessage(true);
                setIsSaving(false);
                return;
            }

            // Ensure data is properly formatted (no double-stringifying)
            // Arrays and objects should already be proper JavaScript objects/arrays
            // Numbers should be numbers, booleans should be booleans, etc.
            const cleanedData = {};
            try {
                const schema = getCollectionSchema(collectionName);
                Object.keys(updateData).forEach(key => {
                    try {
                        // Skip passwordHash for Users collection - it's handled by backend
                        if (collectionName === 'Users' && key === 'passwordHash') {
                            return;
                        }
                        
                        const value = updateData[key];
                        // eslint-disable-next-line no-unused-vars
                        const fieldType = schema && schema.fieldTypes ? schema.fieldTypes[key] : null;
                        
                        // Handle null or undefined values
                        if (value === null || value === undefined) {
                            // For object fields in BlogContent (author, category, postTopic, coverImage), don't send null
                            // The backend expects these to have a structure or be omitted
                            if (collectionName === 'BlogContent' && (key === 'author' || key === 'category' || key === 'postTopic' || key === 'coverImage')) {
                                // Skip null object fields - don't send them
                                return;
                            }
                            
                            // For required fields, keep null/undefined, but for optional fields, skip them
                            try {
                                const isRequired = schema && schema.requiredFields && schema.requiredFields.includes(key);
                                if (isRequired) {
                                    cleanedData[key] = value; // Keep null/undefined for required fields (validation will catch it)
                                }
                            } catch (schemaError) {
                                // If we can't check if required, skip the field
                            }
                            // Skip optional fields that are null/undefined
                            return;
                        }
                        
                        // Special handling for BlogComments blogId and userId - extract _id from populated objects
                        if ((collectionName === 'BlogComments' || collectionName === 'ProductPrices' || collectionName === 'productprices') && (key === 'blogId' || key === 'userId' || key === 'productId')) {
                            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                // If it's a populated object with _id, extract just the _id
                                if (value._id) {
                                    cleanedData[key] = value._id;
                                    return;
                                }
                            }
                            // If it's already a string, use it as-is
                            if (typeof value === 'string') {
                                cleanedData[key] = value;
                                return;
                            }
                        }
                        
                        // Special handling for BlogContent object fields
                        if (collectionName === 'BlogContent') {
                            // Convert populated objects to just their _id for author, category, postTopic
                            if ((key === 'author' || key === 'category' || key === 'postTopic') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                // If it's a populated object with _id, extract just the _id
                                if (value._id) {
                                    cleanedData[key] = value._id;
                                    return;
                                }
                                // If it's already a string/ObjectId, use it as-is
                                if (typeof value === 'string') {
                                    cleanedData[key] = value;
                                    return;
                                }
                                // If it has a value property (backend format), extract it
                                if (value.value) {
                                    cleanedData[key] = value.value;
                                    return;
                                }
                            }
                            
                            // For coverImage, ensure it's a proper object structure
                            if (key === 'coverImage' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                // Ensure it has the expected structure
                                cleanedData[key] = {
                                    url: value.url || '',
                                    fileName: value.fileName || '',
                                    imageTitle: value.imageTitle || value.title || ''
                                };
                                return;
                            }
                        }
                        
                        // Keep the value as-is - it's already in the correct format
                        // Don't stringify arrays/objects - they'll be stringified by JSON.stringify in fetch
                        cleanedData[key] = value;
                    } catch (error) {
                        // Skip this field if there's an error processing it
                    }
                });
            } catch (cleaningError) {
                // Continue anyway - we'll try to save what we have
            }

            const url = getDocUrl(collectionName, documentId);

            const res = await fetch(url, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(cleanedData), // This will properly stringify the entire object
                credentials: "include"
            });

            // Check status FIRST - if OK, always show success regardless of any parsing errors
            if (res.ok) {
                // Success - show success state then redirect
                setIsSaving(false);
                setIsSaveSuccess(true);
                
                // Check if we should return to a multi-select dialog
                const urlParams = new URLSearchParams(location.search);
                const returnToMultiSelect = urlParams.get("returnToMultiSelect");
                const multiSelectReturnPath = localStorage.getItem("multiSelectReturnPath");
                
                setTimeout(() => {
                    if (returnToMultiSelect === "true" && multiSelectReturnPath) {
                        localStorage.removeItem("multiSelectReturnPath");
                        navigate(`${multiSelectReturnPath}${multiSelectReturnPath.includes('?') ? '&' : '?'}returnFromCreate=true&newId=${documentId}`);
                    } else {
                        navigate(`/admin/database/${databaseType}/${collectionName}`);
                    }
                }, 500);
                return;
            }

            // Handle error responses (only if status is NOT OK)
            const contentType = res.headers.get("content-type");
            let errorMessage = `Failed to update document (${res.status})`;
            
            try {
                if (contentType && contentType.includes("application/json")) {
                    try {
                        const errorJson = await res.json();
                        if (errorJson && errorJson !== null && typeof errorJson === 'object') {
                            const message = errorJson.message || errorJson.error;
                            if (message && typeof message === 'string') {
                                errorMessage = message;
                            }
                        }
                    } catch (jsonError) {
                        // Use default error message
                    }
                } else {
                    // If HTML response, extract text content
                    try {
                        const text = await res.text();
                        if (text && text !== null && typeof text === 'string') {
                            if (text.length < 200) {
                                errorMessage = text;
                            } else {
                                // Try to extract meaningful error from HTML
                                const match = text.match(/<pre>(.*?)<\/pre>/i);
                                if (match && match[1]) {
                                    errorMessage = match[1];
                                }
                            }
                        }
                    } catch (textError) {
                        // Use default error message
                    }
                }
            } catch (errorParseError) {
                // If we can't parse the error, use default message
            }
            
            throw new Error(errorMessage);
        } catch (e) {
            // Check if the error message contains "null" or "value" to provide better error handling
            const errorMessage = e.message || 'Failed to save document';
            
            // If it's a null reference error, provide a more helpful message
            if (errorMessage.includes('null') && (errorMessage.includes('value') || errorMessage.includes('reading'))) {
                // Per user request: data saves properly despite this error, so treat as success and redirect
                console.log("Suppressing expected harmless error:", errorMessage);
                navigate(`/admin/database/${databaseType}/${collectionName}`);
                return;
            } else {
                setMessage(`Error: ${errorMessage}`);
                setShowMessage(true);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        const urlParams = new URLSearchParams(location.search);
        const returnToMultiSelect = urlParams.get("returnToMultiSelect");
        const multiSelectReturnPath = localStorage.getItem("multiSelectReturnPath");

        if (returnToMultiSelect === "true" && multiSelectReturnPath) {
            localStorage.removeItem("multiSelectReturnPath");
            navigate(multiSelectReturnPath);
        } else {
            // Reset to original data
            if (documentData) {
                setEditData({ ...documentData });
            }
            setValidationErrors([]);
            navigate(`/admin/database/${databaseType}/${collectionName}`);
        }
    };

    const handleInputChange = (key, value) => {
        // Ensure editData is initialized
        if (!editData || typeof editData !== 'object' || Array.isArray(editData)) {
            setEditData({ [key]: value });
            return;
        }
        
        // Ensure value is not null (can be undefined for optional fields)
        if (value === null) {
            // Convert null to appropriate default based on field type
            const fieldType = getFieldType(collectionName, key);
            if (fieldType === 'array') {
                value = [];
            } else if (fieldType === 'object') {
                value = {};
            } else if (fieldType === 'boolean') {
                value = false;
            } else if (fieldType === 'number') {
                value = 0;
            } else {
                value = '';
            }
        }
        
        // Try to maintain original data type
        let parsedValue = value;
        if (documentData && documentData[key] !== undefined) {
            const originalType = typeof documentData[key];
            if (originalType === 'number') {
                parsedValue = isNaN(Number(value)) ? value : Number(value);
            } else if (originalType === 'boolean') {
                parsedValue = value === 'true' || value === true || value === '1';
            } else if (originalType === 'object' && documentData[key] !== null) {
                // Try to parse as JSON if it looks like JSON
                try {
                    if (typeof value === 'string') {
                        parsedValue = JSON.parse(value);
                    } else {
                        parsedValue = value;
                    }
                } catch {
                    parsedValue = value;
                }
            } else {
                parsedValue = value;
            }
        }
        
        const updatedEditData = { ...editData, [key]: parsedValue };
        
        // If category is changed in BlogContent, filter PostTopics
        if (collectionName === 'BlogContent' && key === 'category') {
            filterPostTopicsByCategory(parsedValue, allPostTopicOptions);
            // Clear postTopic if it doesn't match the new category
            if (editData.postTopic) {
                const selectedCategoryId = typeof parsedValue === 'object' && parsedValue._id ? String(parsedValue._id) : String(parsedValue);
                const currentPostTopicId = typeof editData.postTopic === 'object' && editData.postTopic._id ? String(editData.postTopic._id) : String(editData.postTopic);
                const filteredTopics = allPostTopicOptions.filter(topic => {
                    const topicCategoryId = topic.whichCategory 
                        ? (typeof topic.whichCategory === 'object' && topic.whichCategory._id ? String(topic.whichCategory._id) : String(topic.whichCategory))
                        : null;
                    return topicCategoryId === selectedCategoryId;
                });
                const topicStillValid = filteredTopics.some(topic => String(topic._id) === currentPostTopicId);
                if (!topicStillValid) {
                    updatedEditData.postTopic = '';
                }
            }
        }
        
        setEditData(updatedEditData);
    };

    const handleClearField = (key) => {
        const fieldType = getFieldType(collectionName, key);
        // Clear the value based on field type
        if (fieldType === 'boolean') {
            handleInputChange(key, false);
        } else if (fieldType === 'number') {
            handleInputChange(key, '');
        } else if (fieldType === 'array') {
            handleInputChange(key, []);
        } else if (fieldType === 'object') {
            handleInputChange(key, {});
        } else if (fieldType === 'richtext') {
            handleInputChange(key, { type: 'doc', content: [] });
        } else {
            handleInputChange(key, '');
        }
    };

    const renderFieldHeader = (fieldLabel, isRequired, fieldKey) => {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '8px' 
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {fieldLabel && (
                        <label 
                            htmlFor={fieldKey}
                            style={{ 
                                display: 'block', 
                                fontSize: '14px', 
                                fontWeight: 600,
                                color: '#111827',
                                cursor: 'pointer'
                            }}
                        >
                            {fieldLabel}
                        </label>
                    )}
                    {isRequired && (
                        <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#6b7280'
                        }}>
                            Required
                        </span>
                    )}
                </div>
                {!isViewMode && (
                <span
                    role="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    onClick={() => handleClearField(fieldKey)}
                    style={{
                        padding: '4px 12px',
                        backgroundColor: '#f3f4f6',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6b7280',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        outline: 'none',
                        userSelect: 'none',
                        display: 'inline-block'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f3f4f6';
                    }}
                >
                    Clear
                </span>
                )}
            </div>
        );
    };


    // Render field as view-only (text display) for staff users
    const renderViewField = (key, value) => {
        const fieldLabel = getFieldLabel(collectionName, key);
        const fieldType = getFieldType(collectionName, key);
        let currentValue = editData[key] !== undefined ? editData[key] : (value !== undefined ? value : '');
        
        // Use resolved value if available (for ObjectId references)
        if (isViewMode && resolvedValues[key] !== undefined) {
            currentValue = resolvedValues[key];
        }
        
        // Special handling for rich text content
        const isBlogContentCollection = collectionName && (
            collectionName.toLowerCase() === 'blogcontent' || 
            collectionName.toLowerCase() === 'blogcontents'
        );
        const isProductDetailsCollection = collectionName && (
            collectionName.toLowerCase() === 'productdetails'
        );
        const isPodcastsAudioDetailsCollection = collectionName && (
            collectionName.toLowerCase() === 'podcastsaudiodetails'
        );
        const isCouponCodeCollection = collectionName && (
            collectionName.toLowerCase() === 'couponcode'
        );
        const isAffiliateProductDetailsCollection = collectionName && (
            collectionName.toLowerCase() === 'affiliateproductdetails'
        );
        const isContentField = key.toLowerCase() === 'content';
        const isDescriptionField = (key.toLowerCase() === 'description' && isProductDetailsCollection) || 
                                   (key.toLowerCase() === 'description' && isPodcastsAudioDetailsCollection) ||
                                   (key.toLowerCase() === 'description' && isCouponCodeCollection) ||
                                   (key === 'productDescription' && isAffiliateProductDetailsCollection);
        
        // Handle rich text fields - show as read-only editor
        if ((isBlogContentCollection && isContentField) || isDescriptionField || fieldType === 'richtext' || fieldType === 'tiptap') {
            let tipTapContent = null;
            const contentValue = currentValue;
            
            if (contentValue) {
                if (typeof contentValue === 'string') {
                    try {
                        const parsed = JSON.parse(contentValue);
                        if (parsed && typeof parsed === 'object' && parsed.json && parsed.json.type === 'doc') {
                            tipTapContent = parsed.json;
                        } else if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
                            tipTapContent = parsed;
                        } else if (parsed && typeof parsed === 'object' && parsed.html) {
                            tipTapContent = parsed.html;
                        }
                    } catch {
                        tipTapContent = contentValue;
                    }
                } else if (typeof contentValue === 'object') {
                    if (contentValue.json && typeof contentValue.json === 'object' && contentValue.json.type === 'doc') {
                        tipTapContent = contentValue.json;
                    } else if (contentValue.type === 'doc') {
                        tipTapContent = contentValue;
                    } else if (contentValue.html) {
                        tipTapContent = contentValue.html;
                    }
                }
            }
            
            if (!tipTapContent) {
                tipTapContent = { type: 'doc', content: [] };
            }
            
            return (
                <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        color: '#111827',
                        marginBottom: '8px'
                    }}>
                        {fieldLabel}
                    </div>
                    <div style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '12px',
                        backgroundColor: '#f9fafb'
                    }}>
                        <TipTapEditor
                            value={tipTapContent}
                            navPinned={navPinned}
                            onChange={() => {}}
                            isBlogContent={isBlogContentCollection || isPodcastsAudioDetailsCollection || isCouponCodeCollection || isAffiliateProductDetailsCollection}
                            readOnly={true}
                        />
                    </div>
                </div>
            );
        }
        
        const formatValue = (val) => {
            if (val === null || val === undefined || val === '') {
                return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;
            }
            if (typeof val === 'boolean') {
                return <span style={{ color: val ? '#059669' : '#6b7280' }}>{val ? 'Yes' : 'No'}</span>;
            }
            if (typeof val === 'number') {
                return <span>{val}</span>;
            }
            if (Array.isArray(val)) {
                if (val.length === 0) {
                    return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;
                }
                // Display arrays as comma-separated values
                const displayItems = val.map((item, index) => {
                    if (typeof item === 'object' && item !== null) {
                        // For array of objects, try to extract name/title
                        if (item.name) return item.name;
                        if (item.title) return item.title;
                        if (item.topic) return item.topic;
                        // For array of objects, show a summary
                        return `Item ${index + 1}`;
                    }
                    return String(item);
                });
                return <span>{displayItems.join(', ')}</span>;
            }
            if (typeof val === 'object' && val !== null) {
                // Check if this is a resolved ObjectId (just a string now)
                if (typeof val === 'string' && val.length > 0 && val.length < 100) {
                    // Likely a resolved name/title, display as plain text
                    return <span>{val}</span>;
                }
                
                // Display objects as title and text (key-value pairs)
                const entries = Object.entries(val);
                if (entries.length === 0) {
                    return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;
                }
                return (
                    <div style={{
                        backgroundColor: '#f9fafb',
                        padding: '12px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                    }}>
                        {entries.map(([key, value], index) => {
                            // Format key to be more readable (capitalize first letter, add spaces)
                            const formattedKey = key
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, str => str.toUpperCase())
                                .trim();
                            
                            // Check if this value is an ObjectId that should be resolved
                            let displayValue = value;
                            const subMapping = getReferenceMapping(collectionName, key);
                            
                            if (subMapping && typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value)) {
                                // This is an ObjectId in an object - use resolved value if available
                                const resolvedKey = `${key}_${value}`;
                                if (resolvedValues[resolvedKey] !== undefined) {
                                    displayValue = resolvedValues[resolvedKey];
                                } else {
                                    // Try to resolve it
                                    fetchReference(value, subMapping.collection, subMapping.dbType).then(refDoc => {
                                        if (refDoc) {
                                            const resolvedName = refDoc[subMapping.displayField] || refDoc.name || refDoc.title || refDoc.topic || value;
                                            setResolvedValues(prev => ({ ...prev, [resolvedKey]: resolvedName }));
                                        }
                                    });
                                    displayValue = value; // Show ID while resolving
                                }
                            } else if (value === null || value === undefined || value === '') {
                                displayValue = <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;
                            } else if (typeof value === 'boolean') {
                                displayValue = <span style={{ color: value ? '#059669' : '#6b7280' }}>{value ? 'Yes' : 'No'}</span>;
                            } else if (typeof value === 'object' && !Array.isArray(value)) {
                                // Nested objects - show as JSON for now
                                displayValue = <pre style={{ 
                                    margin: '4px 0 0 0',
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>{JSON.stringify(value, null, 2)}</pre>;
                            } else if (Array.isArray(value)) {
                                // Arrays in objects - show as comma-separated
                                if (value.length === 0) {
                                    displayValue = <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;
                                } else {
                                    displayValue = value.map(item => {
                                        if (typeof item === 'object' && item !== null) {
                                            return item.name || item.title || item.topic || JSON.stringify(item);
                                        }
                                        return String(item);
                                    }).join(', ');
                                }
                            } else {
                                displayValue = String(value);
                            }
                            
                            return (
                                <div key={index} style={{
                                    marginBottom: index < entries.length - 1 ? '12px' : '0',
                                    paddingBottom: index < entries.length - 1 ? '12px' : '0',
                                    borderBottom: index < entries.length - 1 ? '1px solid #e5e7eb' : 'none'
                                }}>
                                    <div style={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: '#111827',
                                        marginBottom: '4px'
                                    }}>
                                        {formattedKey}
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#374151',
                                        wordBreak: 'break-word'
                                    }}>
                                        {displayValue}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            }
            return <span>{String(val)}</span>;
        };
        
        return (
            <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    color: '#111827',
                    marginBottom: '8px'
                }}>
                    {fieldLabel}
                </div>
                <div style={{
                    fontSize: '14px',
                    color: '#374151',
                    padding: '8px 0',
                    wordBreak: 'break-word'
                }}>
                    {formatValue(currentValue)}
                </div>
            </div>
        );
    };

    const renderInputField = (key, value) => {
        // If in view mode, render as text instead of input
        if (isViewMode) {
            return renderViewField(key, value);
        }
        
        const isRequired = isFieldRequired(collectionName, key);
        const fieldType = getFieldType(collectionName, key);
        const fieldLabel = getFieldLabel(collectionName, key);
        const hasError = validationErrors.some(err => err.includes(fieldLabel));
        
        // Use editData value if available, otherwise use the passed value
        const currentValue = editData[key] !== undefined ? editData[key] : value;
        
        if (key === '_id' || key === 'id' || key === '__v') {
            return (
                <div>
                    {renderFieldHeader(fieldLabel, false, key)}
                    <input
                        type="text"
                        value={String(value || '')}
                        disabled
                        readOnly={isViewMode}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: isViewMode ? '#f3f4f6' : '#f3f4f6',
                            color: '#6b7280',
                            fontSize: '14px'
                        }}
                    />
                </div>
            );
        }
        
        // Special handling for BlogComments blogId - show blog title as read-only
        if (collectionName === 'BlogComments' && key === 'blogId') {
            const displayValue = typeof value === 'object' && value !== null && value.title 
                ? value.title 
                : (typeof value === 'string' ? value : String(value || ''));
            const actualId = typeof value === 'object' && value !== null && value._id 
                ? value._id 
                : (typeof value === 'string' ? value : String(value || ''));
            
            return (
                <div>
                    {renderFieldHeader(fieldLabel, false, key)}
                    <input
                        type="text"
                        value={displayValue}
                        disabled
                        readOnly
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            fontSize: '14px'
                        }}
                    />
                    <input
                        type="hidden"
                        value={actualId}
                        onChange={() => {}} // Keep the actual ID for saving
                    />
                </div>
            );
        }
        
        // Special handling for BlogComments userId - show user email as read-only
        if (collectionName === 'BlogComments' && key === 'userId') {
            const displayValue = typeof value === 'object' && value !== null && value.email 
                ? value.email 
                : (typeof value === 'string' ? value : String(value || ''));
            const actualId = typeof value === 'object' && value !== null && value._id 
                ? value._id 
                : (typeof value === 'string' ? value : String(value || ''));
            
            return (
                <div>
                    {renderFieldHeader(fieldLabel, false, key)}
                    <input
                        type="text"
                        value={displayValue}
                        disabled
                        readOnly
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            fontSize: '14px'
                        }}
                    />
                    <input
                        type="hidden"
                        value={actualId}
                        onChange={() => {}} // Keep the actual ID for saving
                    />
                </div>
            );
        }

        // Special handling for Content/content field in BlogContent collection and description in ProductDetails/PodcastsAudioDetails
        const isBlogContentCollection = collectionName && (
            collectionName.toLowerCase() === 'blogcontent' || 
            collectionName.toLowerCase() === 'blogcontents'
        );
        const isProductDetailsCollection = collectionName && (
            collectionName.toLowerCase() === 'productdetails'
        );
        const isPodcastsAudioDetailsCollection = collectionName && (
            collectionName.toLowerCase() === 'podcastsaudiodetails'
        );
        const isCouponCodeCollection = collectionName && (
            collectionName.toLowerCase() === 'couponcode'
        );
        const isProductPricesCollection = collectionName && (
            collectionName.toLowerCase() === 'productprices'
        );

        // Special handling for ProductPrices countryCode and currency fields
        // Special handling for ProductPrices productId to show name instead of ID
        if (isProductPricesCollection && key === 'productId') {
            return (
                <div key={key}>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <select
                        value={currentValue ? (typeof currentValue === 'object' ? (currentValue._id || currentValue.toString()) : currentValue) : ''}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                        disabled={isViewMode}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: isViewMode ? '#f3f4f6' : 'white'
                        }}
                    >
                        <option value="">Select Product</option>
                        {productOptions.map(p => (
                            <option key={p._id} value={p._id}>
                                {p.name || p.title}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }

        const isAffiliateProductDetailsCollection = collectionName && (
            collectionName.toLowerCase() === 'affiliateproductdetails'
        );
        const isContentField = key.toLowerCase() === 'content';
        const isDescriptionField = (key.toLowerCase() === 'description' && isProductDetailsCollection) || 
                                   (key.toLowerCase() === 'description' && isPodcastsAudioDetailsCollection) ||
                                   (key.toLowerCase() === 'description' && isCouponCodeCollection) ||
                                   (key === 'productDescription' && isAffiliateProductDetailsCollection);
        const isOverviewField = key.toLowerCase() === 'overview' && isPodcastsAudioDetailsCollection;
        
        if ((isBlogContentCollection && isContentField) || isDescriptionField || isOverviewField || fieldType === 'richtext' || fieldType === 'tiptap') {
            const contentValue = editData[key] !== undefined ? editData[key] : currentValue;
            // Handle different content formats
            let tipTapContent = null;
            
            if (contentValue) {
                if (typeof contentValue === 'string') {
                    try {
                        // Try to parse as JSON string
                        const parsed = JSON.parse(contentValue);
                        // Check if it has a json property (TipTap format)
                        if (parsed && typeof parsed === 'object' && parsed.json) {
                            // Check if json is valid TipTap format (has type: 'doc')
                            if (parsed.json && parsed.json.type === 'doc') {
                                tipTapContent = parsed.json;
                            } else if (parsed.html) {
                                // If json is empty but html exists, we'll let TipTap parse the HTML
                                tipTapContent = parsed.html;
                            } else {
                                tipTapContent = { type: 'doc', content: [] };
                            }
                        } else if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
                            // Already in TipTap JSON format
                            tipTapContent = parsed;
                        } else {
                             // Split by newline and create paragraphs for each line to preserve formatting
                             const lines = String(contentValue).split('\n');
                             tipTapContent = {
                                 type: 'doc',
                                 content: lines.map(line => ({
                                     type: 'paragraph',
                                     content: line ? [{ type: 'text', text: line }] : []
                                 }))
                             };
                        }
                    } catch {
                        // If it's not JSON, treat as plain text and split by newline
                        const lines = String(contentValue).split('\n');
                        tipTapContent = {
                            type: 'doc',
                            content: lines.map(line => ({
                                type: 'paragraph',
                                content: line ? [{ type: 'text', text: line }] : []
                            }))
                        };
                    }
                } else if (typeof contentValue === 'object') {
                    // Check if it has a json property (like {html: "...", json: {...}})
                    if (contentValue.json && typeof contentValue.json === 'object' && contentValue.json.type === 'doc') {
                        // Valid TipTap JSON format
                        tipTapContent = contentValue.json;
                    } else if (contentValue.json && typeof contentValue.json === 'object' && Object.keys(contentValue.json).length === 0) {
                        // json is empty object {}, try to use HTML if available
                        if (contentValue.html && typeof contentValue.html === 'string' && contentValue.html.trim()) {
                            // Pass HTML string to TipTap - it can parse HTML
                            tipTapContent = contentValue.html;
                        } else {
                            // Empty json, create empty document
                            tipTapContent = { type: 'doc', content: [] };
                        }
                    } else if (contentValue.type === 'doc') {
                        // Already in TipTap JSON format
                        tipTapContent = contentValue;
                    } else if (contentValue.html && typeof contentValue.html === 'string') {
                        // Has HTML but no json, use HTML
                        tipTapContent = contentValue.html;
                    } else {
                        // Try to use the object as-is
                        tipTapContent = contentValue;
                    }
                }
            }

            // If still no content, create empty document
            if (!tipTapContent) {
                tipTapContent = {
                    type: 'doc',
                    content: [
                        {
                            type: 'paragraph',
                            content: []
                        }
                    ]
                };
            }


            return (
                <div>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <TipTapEditor
                        value={tipTapContent}
                        navPinned={navPinned}
                        onChange={(data) => {
                            if (isViewMode) return; // Don't allow changes in view mode
                            try {
                                const { json, html } = data;
                                
                                // Ensure editData is valid
                                if (!editData || typeof editData !== 'object' || Array.isArray(editData)) {
                                    handleInputChange(key, json);
                                    return;
                                }
                                
                                const originalValue = editData[key];
                                const isSocialMedia = databaseType === 'socialmediadb';
                                
                                // Save as {json, html} if it's social media or was already in that format
                                if (isSocialMedia || (originalValue && typeof originalValue === 'object' && originalValue !== null && !Array.isArray(originalValue) && originalValue.html !== undefined)) {
                                    handleInputChange(key, {
                                        json: json,
                                        html: html
                                    });
                                } else {
                                    // Default to just saving the JSON for other collections (like Blog)
                                    handleInputChange(key, json);
                                }
                            } catch (error) {
                                // Fallback: just save the JSON directly
                                handleInputChange(key, data.json || data);
                            }
                        }}
                        isBlogContent={isBlogContentCollection || isPodcastsAudioDetailsCollection || isCouponCodeCollection || isAffiliateProductDetailsCollection}
                        readOnly={isViewMode}
                        onBeforeNavigate={saveFormStateToSession}
                    />
                </div>
            );
        }

        const type = typeof value;
        
        if (type === 'boolean') {
            const boolValue = editData[key] !== undefined ? editData[key] : (currentValue !== undefined ? currentValue : false);
            
            // Special handling for BlogContent boolean fields (isPublished, priorityPost, noIndex) - use radio buttons
            if (collectionName === 'BlogContent' && (key === 'isPublished' || key === 'priorityPost' || key === 'noIndex')) {
                return (
                    <div>
                        {renderFieldHeader(fieldLabel, isRequired, key)}
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name={key}
                                    value="yes"
                                    checked={boolValue === true}
                                    onChange={() => handleInputChange(key, true)}
                                    disabled={isViewMode}
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        cursor: isViewMode ? 'not-allowed' : 'pointer'
                                    }}
                                />
                                <span style={{ fontSize: '14px' }}>Yes</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}>
                                <input
                                    type="radio"
                                    name={key}
                                    value="no"
                                    checked={boolValue === false}
                                    onChange={() => handleInputChange(key, false)}
                                    disabled={isViewMode}
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        cursor: isViewMode ? 'not-allowed' : 'pointer'
                                    }}
                                />
                                <span style={{ fontSize: '14px' }}>No</span>
                            </label>
                        </div>
                        {hasError && (
                            <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                                This field is required
                            </div>
                        )}
                    </div>
                );
            }
            
            // Default dropdown for other boolean fields
            return (
                <div>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <select
                        value={boolValue ? 'true' : 'false'}
                        onChange={(e) => handleInputChange(key, e.target.value === 'true')}
                        disabled={isViewMode}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: isViewMode ? '#f3f4f6' : (hasError ? '#fef2f2' : 'white'),
                            cursor: isViewMode ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <option value="false">false</option>
                        <option value="true">true</option>
                    </select>
                </div>
            );
        }
        
        // Handle enum fields (like role, orderStatus, productType)
        if (fieldType === 'enum') {
            let enumOptions = [];
            if (key === 'role') {
                enumOptions = ['user', 'staff', 'admin'];
            } else if (key === 'orderStatus') {
                enumOptions = ['placed', 'not placed'];
            } else if (key === 'productType') {
                enumOptions = ['physical', 'digital'];
            } else if (key === 'paymentMode') {
                enumOptions = ['LOCAL', 'USD_ONLY'];
            }
            
            if (enumOptions.length > 0) {
                const enumValue = editData[key] !== undefined ? editData[key] : (currentValue || '');
                return (
                    <div>
                        {renderFieldHeader(fieldLabel, isRequired, key)}
                        <select
                            value={enumValue}
                            onChange={(e) => handleInputChange(key, e.target.value)}
                            disabled={isViewMode}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                                borderRadius: '6px',
                                fontSize: '14px',
                                backgroundColor: isViewMode ? '#f3f4f6' : (hasError ? '#fef2f2' : 'white'),
                                cursor: isViewMode ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <option value="">Select {fieldLabel}</option>
                            {enumOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                );
            }
        }

        // Handle object fields (including empty/null objects)
        // Skip description field for PodcastsAudioDetails as we want to handle it as text (even if it has legacy object data)
        const isPodcastDescription = key === 'description' && collectionName === 'PodcastsAudioDetails';
        if ((fieldType === 'object' || (type === 'object' && currentValue !== null && currentValue !== undefined)) && !isPodcastDescription) {
            const objValue = editData[key] !== undefined ? editData[key] : (currentValue || {});
            const objString = typeof objValue === 'object' && objValue !== null ? JSON.stringify(objValue, null, 2) : '';
            const jsonError = jsonErrors[key] || null;
            
            return (
                <div>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <textarea
                        value={objString}
                        onChange={(e) => {
                            if (isViewMode) return;
                            try {
                                if (e.target.value.trim() === '') {
                                    setJsonErrors(prev => ({ ...prev, [key]: null }));
                                    handleInputChange(key, {});
                                } else {
                                    const parsed = JSON.parse(e.target.value);
                                    if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                                        setJsonErrors(prev => ({ ...prev, [key]: null }));
                                        handleInputChange(key, parsed);
                                    } else {
                                        setJsonErrors(prev => ({ ...prev, [key]: 'Value must be an object, not an array or primitive' }));
                                    }
                                }
                            } catch (err) {
                                // Check if it's just incomplete JSON (user is still typing)
                                const trimmed = e.target.value.trim();
                                if (trimmed === '' || trimmed === '{' || trimmed.startsWith('{')) {
                                    setJsonErrors(prev => ({ ...prev, [key]: null })); // Allow incomplete JSON while typing
                                } else {
                                    setJsonErrors(prev => ({ ...prev, [key]: `Invalid JSON: ${err.message}` }));
                                }
                            }
                        }}
                        readOnly={isViewMode}
                        rows={8}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${jsonError || hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontFamily: 'monospace',
                            backgroundColor: isViewMode ? '#f3f4f6' : (jsonError || hasError ? '#fef2f2' : 'white'),
                            lineHeight: '1.5',
                            cursor: isViewMode ? 'not-allowed' : 'text'
                        }}
                        placeholder='Enter JSON object, e.g., {"key": "value", "nested": {"key2": "value2"}}'
                    />
                    {jsonError && (
                        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                            {jsonError}
                        </div>
                    )}
                    {!jsonError && objValue && typeof objValue === 'object' && Object.keys(objValue).length > 0 && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                            Object with {Object.keys(objValue).length} key{Object.keys(objValue).length !== 1 ? 's' : ''}
                        </div>
                    )}
                </div>
            );
        }

        // Handle different field types
        if (fieldType === 'number') {
            const numValue = editData[key] !== undefined && editData[key] !== null ? editData[key] : '';
            return (
                <div>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <input
                        type="number"
                        value={numValue}
                        onChange={(e) => handleInputChange(key, e.target.value === '' ? '' : Number(e.target.value))}
                        readOnly={isViewMode}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: isViewMode ? '#f3f4f6' : (hasError ? '#fef2f2' : 'white'),
                            cursor: isViewMode ? 'not-allowed' : 'text'
                        }}
                    />
                </div>
            );
        }
        
        if (fieldType === 'date') {
            const dateValue = editData[key] && editData[key] !== '' ? new Date(editData[key]).toISOString().split('T')[0] : '';
            return (
                <div>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <input
                        type="date"
                        value={dateValue}
                        onChange={(e) => handleInputChange(key, e.target.value ? new Date(e.target.value).toISOString() : '')}
                        onClick={(e) => {
                            if (isViewMode) return;
                            // Ensure date picker opens on click
                            if (e.target.showPicker) {
                                e.target.showPicker();
                            }
                        }}
                        readOnly={isViewMode}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: isViewMode ? '#f3f4f6' : (hasError ? '#fef2f2' : 'white'),
                            cursor: isViewMode ? 'not-allowed' : 'pointer',
                            position: 'relative'
                        }}
                    />
                </div>
            );
        }
        
        if (fieldType === 'array') {
            const arrayValue = Array.isArray(editData[key]) ? editData[key] : (Array.isArray(currentValue) ? currentValue : []);
            const jsonError = jsonErrors[key] || null;
            
            return (
                <div>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <textarea
                        value={JSON.stringify(arrayValue, null, 2)}
                        onChange={(e) => {
                            if (isViewMode) return;
                            try {
                                const parsed = JSON.parse(e.target.value);
                                if (Array.isArray(parsed)) {
                                    setJsonErrors(prev => ({ ...prev, [key]: null }));
                                    handleInputChange(key, parsed);
                                } else {
                                    setJsonErrors(prev => ({ ...prev, [key]: 'Value must be an array' }));
                                }
                            } catch (err) {
                                // Check if it's just incomplete JSON (user is still typing)
                                const trimmed = e.target.value.trim();
                                if (trimmed === '' || trimmed === '[' || trimmed.startsWith('[')) {
                                    setJsonErrors(prev => ({ ...prev, [key]: null })); // Allow incomplete JSON while typing
                                } else {
                                    setJsonErrors(prev => ({ ...prev, [key]: `Invalid JSON: ${err.message}` }));
                                }
                            }
                        }}
                        readOnly={isViewMode}
                        rows={6}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${jsonError || hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontFamily: 'monospace',
                            backgroundColor: isViewMode ? '#f3f4f6' : (jsonError || hasError ? '#fef2f2' : 'white'),
                            lineHeight: '1.5',
                            cursor: isViewMode ? 'not-allowed' : 'text'
                        }}
                        placeholder='Enter array as JSON, e.g., ["item1", "item2"] or [{"key": "value"}]'
                    />
                    {jsonError && (
                        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                            {jsonError}
                        </div>
                    )}
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                        Array with {arrayValue.length} item{arrayValue.length !== 1 ? 's' : ''}
                    </div>
                </div>
            );
        }
        
        if (fieldType === 'objectid') {
            const objIdValue = editData[key] !== undefined ? String(editData[key] || '') : String(currentValue || '');
            return (
                <div>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <input
                        type="text"
                        value={objIdValue}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                        readOnly={isViewMode}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: isViewMode ? '#f3f4f6' : (hasError ? '#fef2f2' : 'white'),
                            cursor: isViewMode ? 'not-allowed' : 'text'
                        }}
                        placeholder="Enter ObjectId"
                    />
                </div>
            );

        }

        // Handle bio/overview fields with textarea instead of single line input
        if (key.toLowerCase().includes('bio') || key.toLowerCase() === 'overview') {
            // If value is an object (legacy TipTap data), try to show empty string or handle it gracefully
            let textValue = editData[key] !== undefined ? editData[key] : (currentValue || '');
            
            // If it's an object, it might be legacy rich text.
            // For now, if it's an object, we'll just show empty string so user can overwrite it with plain text.
            // Or if we want to be fancy, check for html/text properties.
            if (typeof textValue === 'object' && textValue !== null) {
                // If the user hasn't touched it yet, it might be the complex object.
                // We don't want to show [object Object].
                // We'll show empty string (effectively clearing the legacy format on edit).
                // But we should check if there's any recoverable text?
                // For simplicity/robustness: show empty if object.
                textValue = ''; 
            } else {
                textValue = String(textValue);
            }

            return (
                <div>
                    {renderFieldHeader(fieldLabel, isRequired, key)}
                    <textarea
                        id={key}
                        value={textValue}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                        readOnly={isViewMode}
                        tabIndex={0}
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: isViewMode ? '#f3f4f6' : (hasError ? '#fef2f2' : 'white'),
                            cursor: isViewMode ? 'not-allowed' : 'text',
                            resize: 'none',
                            lineHeight: '1.5',
                            height: '110px',
                            minHeight: '110px',
                            maxHeight: '110px',
                            overflowY: 'auto',
                            fontFamily: 'inherit'
                        }}
                    />
                </div>
            );
        }
        
        const stringValue = editData[key] !== undefined ? String(editData[key] || '') : String(currentValue || '');
        return (
            <div>
                {renderFieldHeader(fieldLabel, isRequired, key)}
                <input
                    id={key}
                    type="text"
                    value={stringValue}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    readOnly={isViewMode}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: isViewMode ? '#f3f4f6' : (hasError ? '#fef2f2' : 'white'),
                        cursor: isViewMode ? 'not-allowed' : 'text',
                        height: 'auto'
                    }}
                />
            </div>
        );
    };

    // Get all keys - use fieldOrder if available, otherwise use schema order
    const getAllKeys = () => {
        const schema = getCollectionSchema(collectionName);
        const fieldOrder = getFieldOrder(collectionName);
        const schemaKeys = schema ? Object.keys(schema.fieldTypes) : [];
        const documentKeys = documentData ? Object.keys(documentData) : [];
        
        // Combine schema keys and document keys, removing duplicates
        const allKeysSet = new Set([...schemaKeys, ...documentKeys]);
        // Always include _id if it exists
        if (documentData && documentData._id) {
            allKeysSet.add('_id');
        }
        
        const allKeys = Array.from(allKeysSet);
        
        // If we have a fieldOrder, use it
        if (fieldOrder) {
            const orderedKeys = [];
            // Add _id first if it exists
            if (allKeysSet.has('_id')) {
                orderedKeys.push('_id');
                allKeysSet.delete('_id');
            }
            // Add fields in the specified order
            fieldOrder.forEach(field => {
                if (allKeysSet.has(field)) {
                    orderedKeys.push(field);
                    allKeysSet.delete(field);
                }
            });
            // Add remaining fields that aren't in the order
            Array.from(allKeysSet).sort().forEach(key => orderedKeys.push(key));
            return orderedKeys;
        }
        
        // Sort: _id first, then required fields, then other schema fields, then remaining fields
        return allKeys.sort((a, b) => {
            if (a === '_id') return -1;
            if (b === '_id') return 1;
            
            if (schema) {
                const aIsRequired = schema.requiredFields.includes(a);
                const bIsRequired = schema.requiredFields.includes(b);
                
                if (aIsRequired && !bIsRequired) return -1;
                if (!aIsRequired && bIsRequired) return 1;
                
                const aInSchema = schemaKeys.includes(a);
                const bInSchema = schemaKeys.includes(b);
                
                if (aInSchema && !bInSchema) return -1;
                if (!aInSchema && bInSchema) return 1;
            }
            
            return a.localeCompare(b);
        });
    };
    
    let keys = getAllKeys();
    
    // Filter out passwordHash for Users collection - it's handled by backend
    if (collectionName === 'Users') {
        keys = keys.filter(key => key !== 'passwordHash');
    }
    
    // Filter out __v field - it's unnecessary
    // Allow createdAt for BlogContent collection only
    keys = keys.filter(key => key !== '__v');
    if (collectionName !== 'BlogContent') {
        keys = keys.filter(key => key !== 'createdAt');
    }
    
    // Filter out updatedAt for CouponCode collection
    if (collectionName === 'CouponCode' || collectionName === 'couponcode') {
        keys = keys.filter(key => key !== 'updatedAt' && key !== 'applicableCurrencies');
    }
    
    // Filter out uploadedAt for ImageAssets collection - it's auto-generated by backend
    if (collectionName === 'ImageAssets' || collectionName === 'imageassets') {
        keys = keys.filter(key => key !== 'uploadedAt');
    }
    
    // Filter out _id for PostTopics collection
    if (collectionName === 'PostTopics' || collectionName === 'posttopics' || collectionName === 'posttopic' ||
        collectionName === 'PodcastsAudioDetails' || collectionName === 'podcastsaudiodetails') {
        keys = keys.filter(key => key !== '_id' && key !== 'id');
    }

    // Filter out emailNotification, publishedAt, publishedBy - system-managed and should not be manually edited
    if (collectionName === 'BlogContent' || collectionName === 'blogcontent' ||
        collectionName === 'PodcastsAudioDetails' || collectionName === 'podcastsaudiodetails') {
        keys = keys.filter(key => key !== 'emailNotification' && key !== 'publishedAt' && key !== 'publishedBy');
    }

    // Special handling for ProductDetails collection
    const isProductDetailsCollection = collectionName && (
        collectionName.toLowerCase() === 'productdetails'
    );
    const isCouponCodeCollection = collectionName && (
        collectionName.toLowerCase() === 'couponcode'
    );

    return (
        <>
        <div className="admin-container w-full max-w-none px-0 pb-[20px]">
            <AdminTopNav activeTab="database" navPinned={navPinned} onTogglePin={() => setNavPinned(v => !v)} />
            <main className="admin-content">
                <div className="admin-content-pad p-[6px]">
                    <div className="admin-card-full-width rounded-[12px] border border-slider bg-bg p-[14px] shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
                            <div style={{ marginBottom: 24 }}>
                                <Link 
                                    to={`/admin/database/${databaseType}/${collectionName}`} 
                                    style={{ color: '#6b7280', textDecoration: 'none', fontSize: '14px' }}
                                >
                                    ← Back to {collectionName} ({databaseName})
                                </Link>
                                <h1 style={{ fontSize: 22, fontWeight: 700, margin: "8px 0", color: "#0f172a" }}>
                                    {isViewMode ? 'View Document' : 'Edit Document'}
                                </h1>
                            </div>


                            {loading && (
                                <div className="form-skeleton-container">
                                    {[
                                        { labelW: '25%', inputH: 40 },
                                        { labelW: '35%', inputH: 40 },
                                        { labelW: '20%', inputH: 40 },
                                        { labelW: '40%', inputH: 140 },
                                        { labelW: '30%', inputH: 40 },
                                        { labelW: '22%', inputH: 40 },
                                    ].map((item, idx) => (
                                        <div key={idx} className="form-skeleton-field">
                                            <div className="form-skeleton-label" style={{ width: item.labelW }}></div>
                                            <div 
                                                className={item.inputH > 40 ? "form-skeleton-textarea" : "form-skeleton-input"} 
                                                style={{ height: `${item.inputH}px` }}
                                            ></div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {error && !loading && (
                                <div style={{ color: "#b91c1c", marginBottom: 16 }}>
                                    {error}
                                </div>
                            )}

                            {!loading && !error && documentData && (
                                <>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                        {keys.map(key => {
                                            // Conditional rendering for stockAvailability - only show when productType is "physical"
                                            if (isProductDetailsCollection && key === 'stockAvailability') {
                                                const productType = editData['productType'] !== undefined ? editData['productType'] : (documentData['productType'] || '');
                                                if (productType !== 'physical') {
                                                    return null; // Don't show stockAvailability if productType is not "physical"
                                                }
                                            }
                                            
                                            // Conditional rendering for displayImage - only show when productType is "digital"
                                            if (isProductDetailsCollection && key === 'displayImage') {
                                                const productType = editData['productType'] !== undefined ? editData['productType'] : (documentData['productType'] || '');
                                                if (productType !== 'digital') {
                                                    return null; // Don't show displayImage if productType is not "digital"
                                                }
                                            }

                                            // Conditional rendering for CouponCode fields
                                            if (isCouponCodeCollection && !isViewMode) {
                                                const discountType = editData['discountType'] !== undefined ? editData['discountType'] : (documentData['discountType'] || 'PERCENT');
                                                if (key === 'discountValue' && discountType !== 'PERCENT') {
                                                    return null;
                                                }
                                                if (key === 'discountByCountry' && discountType !== 'FIXED') {
                                                    return null;
                                                }
                                                if (key === 'minimumOrderValue' && discountType !== 'PERCENT') {
                                                    return null;
                                                }
                                                if (key === 'minimumOrderValueByCountry' && discountType !== 'FIXED') {
                                                    return null;
                                                }
                                            }
                                            
                                            const isRequired = isFieldRequired(collectionName, key);
                                            const fieldLabel = getFieldLabel(collectionName, key);
                                            const fieldType = getFieldType(collectionName, key);
                                            const hasError = validationErrors.some(err => err.includes(fieldLabel));
                                            
                                            // Get the value from editData (which includes defaults for empty fields)
                                            const fieldValue = editData[key] !== undefined ? editData[key] : (documentData[key] !== undefined ? documentData[key] : '');
                                            
                                            return (
                                                <div key={key}>
                                                    {/* In view mode, render all fields as text */}
                                                    {isViewMode ? (
                                                        renderViewField(key, fieldValue)
                                                    ) : (
                                                        <>
                                                            {/* Always render _id as read-only text field */}
                                                            {key === '_id' || key === 'id' ? (
                                                                renderInputField(key, fieldValue)
                                                            ) : (
                                                                <>
                                                                    {/* Use DynamicField for structured fields, renderInputField for special cases */}
                                                                    {((collectionName === 'BlogContent' && key === 'createdAt') || (collectionName === 'PodcastsAudioDetails' && key === 'date')) ? (
                                                                        (() => {
                                                                            const isPostNow = !isScheduled;
                                                                            const handlePostNow = () => {
                                                                                 setIsScheduled(false);
                                                                                 const now = new Date().toISOString();
                                                                                 handleInputChange(key, now);
                                                                                 handleInputChange('isPublished', true);
                                                                                 if (collectionName === 'BlogContent') {
                                                                                     handleInputChange('publishedAt', now);
                                                                                 }
                                                                             };
                                                                            const handleSchedule = () => {
                                                                                setIsScheduled(true);
                                                                                handleInputChange('isPublished', false);
                                                                                const existingDate = editData[key] ? new Date(editData[key]) : null;
                                                                                const isFuture = existingDate && !isNaN(existingDate.getTime()) && existingDate > new Date();
                                                                                const defaultFutureIso = isFuture ? editData[key] : new Date(Date.now() + 3600000).toISOString();
                                                                                handleInputChange(key, defaultFutureIso);
                                                                                if (collectionName === 'BlogContent') {
                                                                                    handleInputChange('publishedAt', defaultFutureIso);
                                                                                }
                                                                            };

                                                                            return (
                                                                              <div key={`${key}-scheduling-wrapper`} style={{ marginBottom: "20px" }}>
                                                                                 {/* Publish Status & Unpublish Option for BlogContent */}
                                                                                 {collectionName === 'BlogContent' && (
                                                                                     <div style={{
                                                                                         marginBottom: "16px",
                                                                                         padding: "12px 16px",
                                                                                         backgroundColor: "#f9fafb",
                                                                                         borderRadius: "8px",
                                                                                         border: "1px solid #e5e7eb",
                                                                                         display: "flex",
                                                                                         alignItems: "center",
                                                                                         justifyContent: "space-between",
                                                                                         flexWrap: "wrap",
                                                                                         gap: "12px"
                                                                                     }}>
                                                                                         <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                                                             <span style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>Publish Status:</span>
                                                                                             <span style={{
                                                                                                 display: "inline-flex",
                                                                                                 alignItems: "center",
                                                                                                 gap: "6px",
                                                                                                 padding: "4px 10px",
                                                                                                 borderRadius: "9999px",
                                                                                                 fontSize: "13px",
                                                                                                 fontWeight: 600,
                                                                                                 backgroundColor: editData.isPublished ? "#ecfdf5" : (isScheduled ? "#eff6ff" : "#fff7ed"),
                                                                                                 color: editData.isPublished ? "#059669" : (isScheduled ? "#2563eb" : "#c2410c"),
                                                                                                 border: `1px solid ${editData.isPublished ? "#a7f3d0" : (isScheduled ? "#bfdbfe" : "#ffedd5")}`
                                                                                             }}>
                                                                                                 <span style={{
                                                                                                     width: "8px",
                                                                                                     height: "8px",
                                                                                                     borderRadius: "50%",
                                                                                                     backgroundColor: editData.isPublished ? "#10b981" : (isScheduled ? "#3b82f6" : "#f97316")
                                                                                                 }}></span>
                                                                                                 {editData.isPublished ? "Published" : (isScheduled ? "Scheduled" : "Unpublished / Draft")}
                                                                                             </span>
                                                                                         </div>

                                                                                         {/* Unpublish Button: ONLY for published blog posts, NOT for scheduled posts */}
                                                                                         {!isViewMode && editData.isPublished === true && (
                                                                                             <button
                                                                                                 type="button"
                                                                                                 onClick={() => {
                                                                                                     handleInputChange('isPublished', false);
                                                                                                     setIsScheduled(true);
                                                                                                     const existingDate = editData[key] ? new Date(editData[key]) : null;
                                                                                                     const isFuture = existingDate && !isNaN(existingDate.getTime()) && existingDate > new Date();
                                                                                                     const defaultFutureIso = isFuture ? editData[key] : new Date(Date.now() + 3600000).toISOString();
                                                                                                     handleInputChange(key, defaultFutureIso);
                                                                                                     if (collectionName === 'BlogContent') {
                                                                                                         handleInputChange('publishedAt', defaultFutureIso);
                                                                                                     }
                                                                                                 }}
                                                                                                 style={{
                                                                                                     padding: "6px 14px",
                                                                                                     borderRadius: "6px",
                                                                                                     border: "1px solid #dc2626",
                                                                                                     backgroundColor: "#fef2f2",
                                                                                                     color: "#dc2626",
                                                                                                     cursor: "pointer",
                                                                                                     fontWeight: 600,
                                                                                                     fontSize: "13px",
                                                                                                     transition: "all 0.2s",
                                                                                                     display: "inline-flex",
                                                                                                     alignItems: "center",
                                                                                                     gap: "6px"
                                                                                                 }}
                                                                                             >
                                                                                                 <i className="fi fi-rr-cross-circle"></i>
                                                                                                 Unpublish Post
                                                                                             </button>
                                                                                         )}
                                                                                     </div>
                                                                                 )}
                                                                                 <div style={{ marginBottom: "12px" }}>
                                                                                    <label style={{ 
                                                                                        display: 'block', 
                                                                                        fontSize: '14px', 
                                                                                        fontWeight: 600, 
                                                                                        color: '#111827',
                                                                                        marginBottom: '8px' 
                                                                                    }}>
                                                                                      Publishing Option
                                                                                    </label>
                                                                                    {!isViewMode ? (
                                                                                    <div style={{ display: "flex", gap: "12px" }}>
                                                                                      <button
                                                                                        type="button"
                                                                                        onClick={handlePostNow}
                                                                                        style={{
                                                                                          padding: "8px 16px",
                                                                                          borderRadius: "6px",
                                                                                          border: `1px solid ${isPostNow ? "#2563eb" : "#e5e7eb"}`,
                                                                                          backgroundColor: isPostNow ? "#eff6ff" : "#fff",
                                                                                          color: isPostNow ? "#2563eb" : "#374151",
                                                                                          cursor: "pointer",
                                                                                          fontWeight: 500,
                                                                                          transition: "all 0.2s",
                                                                                        }}
                                                                                      >
                                                                                        Post Now
                                                                                      </button>
                                                                                      <button
                                                                                        type="button"
                                                                                        onClick={handleSchedule}
                                                                                        style={{
                                                                                            padding: "8px 16px",
                                                                                            borderRadius: "6px",
                                                                                            border: `1px solid ${!isPostNow ? "#2563eb" : "#e5e7eb"}`,
                                                                                            backgroundColor: !isPostNow ? "#eff6ff" : "#fff",
                                                                                            color: !isPostNow ? "#2563eb" : "#374151",
                                                                                            cursor: "pointer",
                                                                                            fontWeight: 500,
                                                                                            transition: "all 0.2s",
                                                                                          }}
                                                                                      >
                                                                                        Schedule
                                                                                      </button>
                                                                                    </div>
                                                                                    ) : (
                                                                                        <div style={{ fontSize: '14px', color: '#374151' }}>
                                                                                            {isScheduled ? 'Scheduled' : 'Posted Immediately'}
                                                                                        </div>
                                                                                    )}
                                                                                 </div>
                                                                                 
                                                                                 {/* Only show date input if Scheduled OR if in View Mode (to see the date) */}
                                                                                 {(isScheduled || isViewMode) && (
                                                                                    <div>
                                                                                        {renderFieldHeader(isScheduled ? 'Scheduled Date & Time' : fieldLabel, isRequired, key)}
                                                                                        <input
                                                                                            type="datetime-local"
                                                                                            value={fieldValue ? (() => {
                                                                                                const date = new Date(fieldValue);
                                                                                                // Adjust for timezone offset to display local time correctly
                                                                                                const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
                                                                                                return localDate.toISOString().slice(0, 16);
                                                                                            })() : ''}
                                                                                            onChange={(e) => {
                                                                                                const dateIso = new Date(e.target.value).toISOString();
                                                                                                handleInputChange(key, dateIso);
                                                                                                if (collectionName === 'BlogContent') {
                                                                                                    handleInputChange('publishedAt', dateIso);
                                                                                                }
                                                                                            }}
                                                                                            readOnly={isViewMode}
                                                                                            style={{
                                                                                                width: 'auto',
                                                                                                maxWidth: '100%',
                                                                                                padding: '8px 12px',
                                                                                                border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                                                                                                borderRadius: '6px',
                                                                                                fontSize: '14px',
                                                                                                backgroundColor: isViewMode ? '#f3f4f6' : (hasError ? '#fef2f2' : 'white'),
                                                                                                cursor: isViewMode ? 'not-allowed' : 'pointer',
                                                                                                marginTop: '8px'
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                 )}
                                                                              </div>
                                                                            );
                                                                        })()
                                                                    ) : (
                                                                        fieldType === 'richtext' || (key === 'content' && collectionName === 'BlogContent') || (key === 'description' && collectionName === 'ProductDetails') || (key === 'description' && collectionName === 'CouponCode') || (key === 'description' && collectionName === 'PodcastsAudioDetails') || (collectionName && collectionName.toLowerCase() === 'productprices' && (key === 'productId')) ? (
                                                                        renderInputField(key, fieldValue)
                                                                    ) : (
                                                                        <DynamicField
                                                                            label={fieldLabel}
                                                                            value={fieldValue}
                                                                            onChange={(newValue) => handleInputChange(key, newValue)}
                                                                            fieldType={fieldType}
                                                                            fieldName={key}
                                                                            hasError={hasError}
                                                                            collectionName={collectionName}
                                                                            authorOptions={authorOptions}
                                                                            categoryOptions={collectionName === 'PostTopics' && key === 'whichCategory' ? postTopicCategoryOptions : categoryOptions}
                                                                            postTopicOptions={postTopicOptions}
                                                                             productOptions={productOptions}
                                                                            isRequired={isRequired}
                                                                            documentId={documentId}
                                                                            onRefresh={fetchDocument}
                                                                            readOnly={isViewMode}
                                                                            allData={editData}
                                                                            onUpdateField={handleInputChange}
                                                                            onBeforeNavigate={saveFormStateToSession}
                                                                        />
                                                                    ))}
                                                                </>
                                                            )}
                                                            {hasError && (
                                                                <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                                                                    This field is required
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Email Sent — read-only display for BlogContent and PodcastsAudioDetails */}
                                    {(collectionName === 'BlogContent' || collectionName === 'blogcontent' ||
                                      collectionName === 'PodcastsAudioDetails' || collectionName === 'podcastsaudiodetails') && (
                                        <div className="edit-doc-email-notification">
                                            <div className="edit-doc-email-notification-label">Email Sent</div>
                                            {(() => {
                                                const raw = documentData && documentData.emailNotification;
                                                const entries = Array.isArray(raw)
                                                    ? raw
                                                    : (raw && raw.sentAt ? [raw] : []);
                                                if (entries.length === 0) {
                                                    return <div className="edit-doc-email-notification-empty">Not sent yet</div>;
                                                }
                                                return entries.map((en, i) => (
                                                    <div key={i} className="edit-doc-email-notification-sent">
                                                        <span className="edit-doc-email-notification-template">{en.templateName || 'Notification'}</span>
                                                        <br />
                                                        {new Date(en.sentAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                                                        {en.successCount != null && (
                                                            <span className="edit-doc-email-notification-count"> · {en.successCount} subscriber{en.successCount !== 1 ? 's' : ''} reached</span>
                                                        )}
                                                        {en.failCount > 0 && (
                                                            <span className="edit-doc-email-notification-fail"> · {en.failCount} failed</span>
                                                        )}
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 12,
                                        marginTop: 32,
                                        paddingTop: 24,
                                        borderTop: '1px solid #e5e7eb'
                                    }}>
                                        {!isViewMode && (
                                        <div ref={buttonBarRef} style={{
                                            display: 'flex',
                                            gap: 12,
                                            alignItems: 'center'
                                        }}>
                                            <button
                                                onClick={handleSave}
                                                disabled={isSaving || isSaveSuccess}
                                                className="small-button"
                                                style={{
                                                    padding: '8px 20px',
                                                    height: '36px',
                                                    backgroundColor: isSaveSuccess ? '#059669' : (isSaving ? '#9ca3af' : '#059669'),
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: (isSaving || isSaveSuccess) ? 'not-allowed' : 'pointer',
                                                    fontWeight: 500,
                                                    fontSize: '14px',
                                                    minHeight: '36px !important',
                                                    minWidth: 'auto !important',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <i className="fi fi-br-spinner" style={{
                                                            animation: 'spin 1s linear infinite',
                                                            fontSize: '16px'
                                                        }}></i>
                                                        <span>Saving...</span>
                                                    </>
                                                ) : isSaveSuccess ? (
                                                    <>
                                                        <i className="fi fi-rr-check" style={{ fontSize: '16px' }}></i>
                                                        <span>Success</span>
                                                    </>
                                                ) : (
                                                    'Save'
                                                )}
                                            </button>
                                            <button
                                                onClick={handleCancel}
                                                disabled={isSaving}
                                                className="small-button"
                                                style={{
                                                    padding: '8px 20px',
                                                    height: '36px',
                                                    backgroundColor: isSaving ? '#9ca3af' : '#6b7280',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: isSaving ? 'not-allowed' : 'pointer',
                                                    fontWeight: 500,
                                                    fontSize: '14px',
                                                    minHeight: '36px !important',
                                                    minWidth: 'auto !important'
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        )}
                                        
                                        {/* Success/Error message with timer - displayed below buttons */}
                                        {showMessage && (
                                            <div style={{
                                                padding: '16px',
                                                backgroundColor: message.includes('Error') ? '#fee2e2' : '#d1fae5',
                                                border: `1px solid ${message.includes('Error') ? '#fecaca' : '#a7f3d0'}`,
                                                borderRadius: '8px',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: message.includes('Error') ? '#991b1b' : '#065f46',
                                                    marginBottom: '8px'
                                                }}>
                                                    {message}
                                                </div>
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: '3px',
                                                    backgroundColor: message.includes('Error') ? '#fecaca' : '#a7f3d0',
                                                    overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        position: 'absolute',
                                                        right: 0,
                                                        top: 0,
                                                        height: '100%',
                                                        width: `${timerProgress}%`,
                                                        backgroundColor: message.includes('Error') ? '#dc2626' : '#059669',
                                                        transition: 'width 0.1s linear'
                                                    }} />
                                                </div>
                                            </div>
                                        )}
                                        
                                        {validationErrors.length > 0 && (
                                            <div style={{
                                                padding: '12px',
                                                backgroundColor: '#fef2f2',
                                                border: '1px solid #fecaca',
                                                borderRadius: '6px',
                                                color: '#991b1b',
                                                fontSize: '14px'
                                            }}>
                                                <strong>Please fix the following errors:</strong>
                                                <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                                                    {validationErrors.map((error, idx) => (
                                                        <li key={idx}>{error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
            </main>
        </div>

        {!isViewMode && !loading && showFixedBar && (
            <div className="admin-fixed-action-bar fixed bottom-[12px] right-[12px] z-[60] rounded-[10px] border border-slider bg-bg p-[8px] shadow-[0_4px_16px_rgba(0,0,0,0.15)]">
                <button
                    onClick={handleSave}
                    disabled={isSaving || isSaveSuccess}
                    className="small-button"
                    style={{
                        padding: '8px 20px',
                        height: '36px',
                        backgroundColor: isSaveSuccess ? '#059669' : (isSaving ? '#9ca3af' : '#059669'),
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (isSaving || isSaveSuccess) ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                    }}
                >
                    {isSaving ? (
                        <>
                            <i className="fi fi-br-spinner" style={{ animation: 'spin 1s linear infinite', fontSize: '16px' }}></i>
                            <span>Saving...</span>
                        </>
                    ) : isSaveSuccess ? (
                        <>
                            <i className="fi fi-rr-check" style={{ fontSize: '16px' }}></i>
                            <span>Success</span>
                        </>
                    ) : (
                        'Save'
                    )}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="small-button"
                    style={{
                        padding: '8px 20px',
                        height: '36px',
                        backgroundColor: isSaving ? '#9ca3af' : '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        fontSize: '14px'
                    }}
                >
                    Cancel
                </button>
            </div>
        )}
        </>
    );
}

